// CAN THIS DOCUMENT ACTUALLY BE SENT?
//
// CONSUMER: shared (the prep screen + node-pool tests).
//
// The prep screen disables Send, Send in person and Save map whenever a
// placed field is unusable. This module is the single statement of what
// "unusable" means, so the screen and the tests cannot drift apart —
// they did not drift, and 25 of the 27 Realty forms were unsendable for
// days with nothing going red, because nothing anywhere asked the
// question "can this form be sent?"
//
// WHAT BLOCKS, AND WHAT DELIBERATELY NO LONGER DOES (2026-09-29)
//
// Two things block, and the server refuses both: a pick-one with no
// choices, and a choose-one button in no group. Saying them here is
// saying them before the click, on the screen where they can be fixed.
//
// An EMPTY LOCKED BOX no longer blocks. It did, in two versions: every
// empty one (until 2026-09-18), then only one with no fill rule (the
// oversight case: a box somebody placed and forgot). Both guards were
// written for a screen on which "Accept all" created every suggested
// box as a real, empty field — and on a real send the screen also lost
// each box's fill rule on the way from the server, so Grant's own
// purchase contract opened with "107 locked boxes have nothing typed in
// them yet" (2026-09-29) with the 09-18 fix in place.
//
// Under the rule that replaced that screen (shared/esign/ghost-offers.ts)
// a box exists only because the sender accepted, typed, or handed it;
// an untouched blank is a ghost, is never sent, and is counted out loud
// at Send. So the oversight the guard caught cannot happen, and Grant
// retired it ("go with your recommendations", the drawing's doctrine
// change). What this deliberately accepts is unchanged from 09-18: a
// form can go out with a blank the sender saw and chose to leave, and
// the send line says how many.

/** The shape this module needs. Structural on purpose: the prep screen's
 *  field and a field built straight from the atlas both satisfy it, so
 *  the test can ask the real question without building a React tree. */
export interface SendReadinessField {
  signerKey: string;
  fieldType: string;
  value?: string | null;
  mergeToken?: string | null;
  fillRule?: string | null;
  options?: readonly string[] | null;
  groupName?: string | null;
}

import { isPreparerField } from "./field-types";

/** Why this one field stops the send, or null when it does not.
 *
 *  Returns a reason rather than a boolean so a caller can say which of
 *  the two problems it is without re-deriving it. */
export function sendBlockReason(
  f: SendReadinessField,
): "empty_list" | "ungrouped_button" | null {
  // A pick-one with nothing to pick and a choose-one in no group are
  // both refused by the server too; this is that refusal said early, on
  // the screen where it can still be fixed.
  if (f.fieldType === "dropdown" && (f.options ?? []).filter((o) => o.trim().length > 0).length === 0) {
    return "empty_list";
  }
  if (f.fieldType === "radio" && (f.groupName ?? "").trim().length === 0) {
    return "ungrouped_button";
  }
  return null;
}

/** Every field standing between this document and the Send button. */
export function fieldsBlockingSend<T extends SendReadinessField>(
  fields: readonly T[],
): T[] {
  return fields.filter((f) => sendBlockReason(f) !== null);
}

/** Is this field's owner a problem for the send — placed for a signer
 *  who isn't checked?
 *
 *  A preparer box (the sender's own locked name/date/etc. lines) is
 *  exempt by definition, the same as the send route already treats it
 *  (esign.ts's eligible-signer check) — it belongs to nobody on the
 *  signer list. Until 2026-09-22 the prep screen's OWN pre-send check
 *  didn't know that, so any document with a locked box refused to send
 *  with "someone not on this record", naming nobody, because there was
 *  no unchecked signer to find. */
export function isUnselectedSignerField(
  f: Pick<SendReadinessField, "signerKey"> & { signerKey: string },
  selected: ReadonlySet<string>,
): boolean {
  return !isPreparerField(f) && !selected.has(f.signerKey);
}
